<template>
  <div class="dtc-office-panel">

    <!-- ══════════════════════════════════════════
         ÜBERSICHTSLISTE
    ══════════════════════════════════════════ -->
    <v-card v-if="!selected" class="status-overview-card mb-6">
      <v-card-title class="d-flex align-center">
        <v-icon color="teal" size="24" class="mr-2">mdi-package-variant-closed</v-icon>
        DTC – Office-Freigabe
        <v-spacer></v-spacer>
        <v-chip color="teal" size="small">{{ gefiltertePaletten.length }} Paletten</v-chip>
      </v-card-title>

      <v-divider class="card-divider"></v-divider>

      <!-- Tabs: Aktiv / Archiv -->
      <v-tabs v-model="viewTab" bg-color="grey-darken-4">
        <v-tab value="aktiv">
          <v-icon start>mdi-clock-outline</v-icon>
          Aktive Paletten
        </v-tab>
        <v-tab value="archiv">
          <v-icon start>mdi-archive</v-icon>
          Archiv
        </v-tab>
      </v-tabs>

      <v-card-text class="py-4">
        <v-text-field
          v-model="suche"
          label="Paletten suchen"
          variant="outlined"
          bg-color="rgba(30, 30, 40, 0.5)"
          hide-details="auto"
          clearable
          class="form-field mb-4"
          color="teal"
          hint="Suche nach Paletten-Nr, Lieferschein oder Bestellnummer"
        >
          <template #prepend-inner>
            <v-icon color="teal" size="small">mdi-magnify</v-icon>
          </template>
        </v-text-field>

        <v-data-table
          :headers="palettenHeaders"
          :items="gefiltertePaletten"
          :loading="loading"
          item-value="id"
          class="elevation-0"
        >
          <!-- Status -->
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              v-if="item.status === 'office_freigabe'"
              size="small" color="indigo" variant="flat"
            >
              <v-icon start size="small">mdi-clipboard-check</v-icon>
              Zur Prüfung
            </v-chip>
            <v-chip
              v-else-if="item.status === 'versand_final'"
              size="small" color="blue-darken-1" variant="flat"
            >
              <v-icon start size="small">mdi-truck-delivery</v-icon>
              An Versand
            </v-chip>
            <v-chip
              v-else-if="item.status === 'abgeschlossen'"
              size="small" color="green" variant="flat"
            >
              <v-icon start size="small">mdi-check-circle</v-icon>
              Abgeschlossen
            </v-chip>
          </template>

          <!-- Paletten-Nr anklickbar -->
          <template v-slot:[`item.paletten_nr`]="{ item }">
            <a
              class="palette-link"
              :class="item.status === 'versand_final' || item.status === 'abgeschlossen' ? 'text-grey' : 'text-indigo-darken-1'"
              @click="openPalette(item)"
            >{{ item.paletten_nr }}</a>
          </template>

          <!-- Typ statt Arbeits-Nr -->
          <template v-slot:[`item.paletten_typ`]="{ item }">
            <v-chip
              :color="item.paletten_typ === 'A' ? 'teal' : 'blue'"
              size="small" variant="flat"
            >{{ item.paletten_typ }}-Ware</v-chip>
          </template>

          <!-- Lieferschein -->
          <template v-slot:[`item.lieferschein`]="{ item }">
            <span v-if="item.lieferschein" class="text-medium-emphasis">{{ item.lieferschein }}</span>
            <span v-else class="text-grey">—</span>
          </template>

          <!-- Erstellt am -->
          <template v-slot:[`item.erfassung_datum`]="{ item }">
            {{ item.erfassung_datum ? new Date(item.erfassung_datum).toLocaleDateString('de-DE') : '—' }}
          </template>

          <!-- Artikel -->
          <template v-slot:[`item.anzahl_positionen`]="{ item }">
            <v-chip size="small" color="info">{{ item.anzahl_positionen }} Artikel</v-chip>
          </template>

          <!-- Stückzahl -->
          <template v-slot:[`item.gesamtgewicht`]="{ item }">
            {{ parseFloat(item.gesamtgewicht) ? parseFloat(item.gesamtgewicht).toFixed(2) + ' kg' : '—' }}
          </template>

          <!-- Aktionen -->
          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip v-if="item.status === 'versand_final'" text="Palette an Versand – Details anzeigen">
              <template #activator="{ props }">
                <v-btn v-bind="props" icon="mdi-eye" size="small" variant="text" color="blue" @click="openPalette(item)"></v-btn>
              </template>
            </v-tooltip>
            <v-tooltip v-else-if="item.status === 'abgeschlossen'" text="Archiv – Details anzeigen">
              <template #activator="{ props }">
                <v-btn v-bind="props" icon="mdi-eye" size="small" variant="text" color="grey" @click="openPalette(item)"></v-btn>
              </template>
            </v-tooltip>
            <v-btn
              v-else
              icon="mdi-clipboard-edit"
              size="small"
              variant="text"
              color="indigo"
              @click="openPalette(item)"
              title="Palette bearbeiten"
            ></v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- ══════════════════════════════════════════
         DETAIL-ANSICHT (wie Refurbish)
    ══════════════════════════════════════════ -->
    <div v-if="selected">
      <v-btn class="mb-4" prepend-icon="mdi-arrow-left" @click="backToList">
        Zurück zur Übersicht
      </v-btn>

      <v-row>
        <!-- LINKS: SAP-Bearbeitung -->
        <v-col cols="12" md="6">
          <v-card class="mb-4">
            <v-card-title class="bg-teal-darken-2 d-flex align-center">
              <v-icon class="mr-2">mdi-clipboard-list</v-icon>
              SAP Bearbeitung
              <v-spacer></v-spacer>
              <v-chip size="small" variant="outlined" color="white">{{ selected.paletten_nr }}</v-chip>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>

              <!-- CMMF / Mengen in 23er-Paketen kopieren (kumuliert nach CMMF) -->
              <div class="text-caption text-medium-emphasis mb-2">
                <template v-if="buchungsZeilen.length === 0">Keine buchbaren Zeilen</template>
                <template v-else>
                  Paket {{ sapChunkIndex + 1 }} von {{ sapChunkCount }}
                  · Zeilen {{ sapChunkFrom }}–{{ sapChunkTo }} von {{ buchungsZeilen.length }}
                  (kumuliert, max. 23)
                </template>
              </div>
              <v-btn
                block
                color="primary"
                variant="tonal"
                class="mb-2"
                prepend-icon="mdi-content-copy"
                :disabled="!buchungsZeilen.length"
                @click="copyCmmfChunk"
              >
                {{ sapChunkIndex === 0 ? 'Die ersten 23 CMMF kopieren' : 'Die nächsten 23 CMMF kopieren' }}
              </v-btn>
              <v-btn
                block
                color="indigo"
                variant="tonal"
                class="mb-2"
                prepend-icon="mdi-numeric"
                :disabled="!buchungsZeilen.length"
                @click="copyMengenChunk"
              >
                {{ sapChunkIndex === 0 ? 'Die ersten 23 Mengen kopieren' : 'Die nächsten 23 Mengen kopieren' }}
              </v-btn>
              <div v-if="sapChunkCount > 1" class="d-flex ga-2 mb-4">
                <v-btn
                  size="small"
                  variant="text"
                  :disabled="sapChunkIndex === 0"
                  @click="goPrevSapChunk"
                >
                  Vorheriges Paket
                </v-btn>
                <v-spacer />
                <v-btn
                  size="small"
                  variant="text"
                  :disabled="sapChunkIndex >= sapChunkCount - 1"
                  @click="advanceSapChunk"
                >
                  Nächstes Paket
                </v-btn>
              </div>
              <div v-else class="mb-4"></div>

              <!-- Positionen-Tabelle -->
              <v-table density="comfortable" class="mb-4">
                <thead>
                  <tr>
                    <th>CMMF</th>
                    <th>SKU / Beschreibung</th>
                    <th>Tracking</th>
                    <th class="text-center">Menge</th>
                    <th class="text-center">Gewicht</th>
                    <th>Klasse</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="pos in positionenNormal" :key="pos.id">
                    <td><code>{{ stripCmmf(pos.cmmf) || '—' }}</code></td>
                    <td>
                      <div class="text-body-2">{{ pos.sku }}</div>
                      <div v-if="pos.product_desc" class="text-caption text-grey">{{ pos.product_desc }}</div>
                    </td>
                    <td class="text-caption">{{ pos.tracking_number || '—' }}</td>
                    <td class="text-center">
                      <v-chip size="small" color="primary">{{ pos.menge }}×</v-chip>
                    </td>
                    <td class="text-center" style="min-width:140px">
                      <div v-if="parseFloat(pos.katalog_gewicht)" class="text-body-2">
                        {{ parseFloat(pos.katalog_gewicht).toFixed(3) }} kg
                        <div v-if="pos.menge > 1" class="text-caption text-success">
                          ({{ (parseFloat(pos.katalog_gewicht) * pos.menge).toFixed(2) }} kg)
                        </div>
                        <v-btn
                          v-if="selected.status !== 'versand_final' && pos.cmmf"
                          size="x-small" variant="text" color="teal"
                          icon="mdi-pencil" class="mt-1"
                          @click="gewichtNachtragen(pos)"
                        ></v-btn>
                      </div>
                      <div v-else>
                        <v-btn
                          size="x-small" color="warning" variant="outlined"
                          :disabled="selected.status === 'versand_final' || !pos.cmmf"
                          @click="gewichtNachtragen(pos)"
                        >
                          <v-icon start size="small">mdi-pencil</v-icon>
                          Gewicht nachtragen
                        </v-btn>
                      </div>
                    </td>
                    <td>
                      <v-chip size="x-small" :color="pos.ware_klasse === 'A' ? 'teal' : pos.ware_klasse === 'B' ? 'blue' : 'orange'">
                        {{ pos.ware_klasse }}
                      </v-chip>
                    </td>
                  </tr>

                  <!-- Zu erzeugende Artikel (IRMAF) – getrennt, aber mitgezählt -->
                  <template v-if="positionenErzeugung.length">
                    <tr class="erzeugung-trenner">
                      <td colspan="6" class="text-caption font-weight-bold py-2">
                        <v-icon size="small" color="orange" class="mr-1">mdi-asterisk</v-icon>
                        Zu erzeugende Artikel (IRMAF)
                      </td>
                    </tr>
                    <tr v-for="pos in positionenErzeugung" :key="pos.id" class="erzeugung-row">
                      <td><code>* {{ stripCmmf(pos.cmmf) || '—' }}</code></td>
                      <td>
                        <div class="text-body-2">{{ pos.sku }}</div>
                        <div v-if="pos.product_desc" class="text-caption text-grey">{{ pos.product_desc }}</div>
                      </td>
                      <td class="text-caption">{{ pos.tracking_number || '—' }}</td>
                      <td class="text-center">
                        <v-chip size="small" color="orange">{{ pos.menge }}×</v-chip>
                      </td>
                      <td class="text-center" style="min-width:140px">
                        <div v-if="parseFloat(pos.katalog_gewicht)" class="text-body-2">
                          {{ parseFloat(pos.katalog_gewicht).toFixed(3) }} kg
                          <div v-if="pos.menge > 1" class="text-caption text-success">
                            ({{ (parseFloat(pos.katalog_gewicht) * pos.menge).toFixed(2) }} kg)
                          </div>
                          <v-btn
                            v-if="selected.status !== 'versand_final' && pos.cmmf"
                            size="x-small" variant="text" color="orange"
                            icon="mdi-pencil" class="mt-1"
                            @click="gewichtNachtragen(pos)"
                          ></v-btn>
                        </div>
                        <div v-else>
                          <v-btn
                            size="x-small" color="warning" variant="outlined"
                            :disabled="selected.status === 'versand_final' || !pos.cmmf"
                            @click="gewichtNachtragen(pos)"
                          >
                            <v-icon start size="small">mdi-pencil</v-icon>
                            Gewicht nachtragen
                          </v-btn>
                        </div>
                      </td>
                      <td>
                        <v-chip size="x-small" :color="pos.ware_klasse === 'A' ? 'teal' : pos.ware_klasse === 'B' ? 'blue' : 'orange'">
                          {{ pos.ware_klasse }}
                        </v-chip>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </v-table>

              <v-alert type="info" variant="tonal" density="compact" class="mb-3">
                <strong>{{ positionen.length }} Position(en)</strong> ·
                <strong>{{ gesamtMenge }} Stück</strong> ·
                {{ gesamtGewicht ? gesamtGewicht.toFixed(2) + ' kg' : 'kein Gewicht' }}
                <span v-if="positionenErzeugung.length" class="text-caption">
                  · davon <strong>{{ positionenErzeugung.length }}</strong> zu erzeugen<span class="text-orange"> *</span>
                </span>
              </v-alert>

              <!-- DTC-Notiz (readonly) -->
              <v-textarea
                v-if="selected.dtc_notiz"
                :model-value="selected.dtc_notiz"
                label="Notizen von DTC"
                variant="outlined"
                hide-details
                rows="2"
                class="mb-3"
                color="orange"
                readonly
              >
                <template #prepend-inner><v-icon color="orange" size="small">mdi-information-outline</v-icon></template>
              </v-textarea>

              <!-- Office-Notiz -->
              <v-textarea
                v-model="selected.office_notiz"
                label="Office-Notiz"
                variant="outlined"
                hide-details
                rows="3"
                color="teal"
                :disabled="selected.status === 'versand_final'"
                @blur="saveNote"
              >
                <template #prepend-inner><v-icon color="teal" size="small">mdi-note-text</v-icon></template>
              </v-textarea>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- RECHTS: Lieferschein -->
        <v-col cols="12" md="6">
          <v-card class="mb-4">
            <v-card-title>
              <v-icon class="mr-2">mdi-file-document</v-icon>
              Lieferschein
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-text-field
                v-model="selected.belegdatum"
                label="Belegdatum"
                type="date"
                variant="outlined"
                hide-details
                class="mb-3"
                color="teal"
                prepend-inner-icon="mdi-calendar"
                :disabled="selected.status === 'versand_final'"
              ></v-text-field>
              <v-text-field
                v-model="selected.lieferschein"
                label="Lieferschein-Nummer"
                variant="outlined"
                hide-details
                class="mb-3"
                color="teal"
                prepend-inner-icon="mdi-file-document-outline"
                :disabled="selected.status === 'versand_final'"
              ></v-text-field>
              <v-text-field
                v-model="selected.bestellnummer"
                label="Bestellnummer"
                variant="outlined"
                hide-details
                class="mb-4"
                color="teal"
                prepend-inner-icon="mdi-cart"
                :disabled="selected.status === 'versand_final'"
              ></v-text-field>

              <!-- Lieferschein-Vorschau -->
              <v-card variant="outlined" class="mb-4">
                <v-card-title class="text-subtitle-2 bg-grey-darken-3 pa-2">Vorschau</v-card-title>
                <v-card-text class="pa-3">
                  <table style="width:100%; border-collapse:collapse">
                    <tbody>
                      <tr><td class="text-grey text-caption pr-3">Paletten-Nr:</td><td><strong>{{ selected.paletten_nr }}</strong></td></tr>
                      <tr><td class="text-grey text-caption pr-3">Typ:</td><td>{{ selected.paletten_typ }}-Ware</td></tr>
                      <tr><td class="text-grey text-caption pr-3">Belegdatum:</td><td>{{ selected.belegdatum || '—' }}</td></tr>
                      <tr><td class="text-grey text-caption pr-3">Lieferschein:</td><td>{{ selected.lieferschein || '—' }}</td></tr>
                      <tr><td class="text-grey text-caption pr-3">Bestellnummer:</td><td>{{ selected.bestellnummer || '—' }}</td></tr>
                      <tr><td class="text-grey text-caption pr-3">Gewicht:</td><td>{{ gesamtGewicht ? gesamtGewicht.toFixed(2) + ' kg' : '—' }}</td></tr>
                    </tbody>
                  </table>
                  <v-divider class="my-3"></v-divider>
                  <div class="text-caption text-grey mb-1">Artikel (kumuliert):</div>
                  <table style="width:100%; border-collapse:collapse" class="text-caption">
                    <thead><tr><th class="text-left">CMMF</th><th class="text-left">SKU</th><th class="text-center">Menge</th></tr></thead>
                    <tbody>
                      <tr v-for="(pos, idx) in buchungsZeilen" :key="idx">
                        <td><code>{{ pos.erzeugung ? '* ' : '' }}{{ pos.cmmf || '—' }}</code></td>
                        <td>{{ pos.sku }}</td>
                        <td class="text-center">{{ pos.menge }}</td>
                      </tr>
                    </tbody>
                  </table>
                </v-card-text>
              </v-card>

              <v-btn
                block color="teal" size="large" class="mb-2"
                prepend-icon="mdi-content-save"
                :loading="saving"
                :disabled="selected.status === 'versand_final'"
                @click="speichern"
              >Lieferschein speichern</v-btn>
            </v-card-text>
          </v-card>

          <!-- Aktionen -->
          <v-card>
            <v-card-text>
              <v-btn
                v-if="selected.status === 'office_freigabe'"
                block color="indigo-darken-1" size="large" class="mb-2"
                prepend-icon="mdi-arrow-right"
                :disabled="!selected.lieferschein"
                @click="freigabeVersand"
              >An Versand freigeben</v-btn>
              <v-alert v-else-if="selected.status === 'versand_final'" type="info" variant="tonal" density="compact" class="mb-2">
                <v-icon start>mdi-truck-delivery</v-icon> Palette ist im Versand (nur lesbar)
              </v-alert>
              <v-btn
                block color="warning" size="large" variant="outlined"
                prepend-icon="mdi-arrow-left"
                @click="zurueck"
              >Zurück an DTC</v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Dialog: Gewicht nachtragen (wie Refurbish/Office) -->
    <v-dialog v-model="gewichtDialog" max-width="500">
      <v-card>
        <v-card-title class="bg-warning text-white">
          <v-icon class="mr-2">mdi-weight-kilogram</v-icon>
          Gewicht nachtragen
        </v-card-title>
        <v-card-text v-if="gewichtPos" class="pt-4">
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            <div class="text-body-2"><strong>{{ gewichtPos.product_desc || gewichtPos.sku || '—' }}</strong></div>
            <div class="text-caption">CMMF: {{ stripCmmf(gewichtPos.cmmf) || '—' }}</div>
          </v-alert>

          <v-text-field
            v-model.number="neuesGewicht"
            label="Gewicht in kg"
            type="number"
            step="0.001"
            min="0"
            variant="outlined"
            prepend-inner-icon="mdi-weight-kilogram"
            suffix="kg"
            autofocus
            @keydown.enter.prevent="gewichtSpeichern"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="gewichtDialog = false">Abbrechen</v-btn>
          <v-btn color="success" variant="elevated" :loading="gewichtSaving" @click="gewichtSpeichern">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { API_BASE_URL } from '@/config';

const toast = useToast();
const API = `${API_BASE_URL}dtc/index.php`;

const paletten = ref([]);
const loading = ref(false);
const saving = ref(false);
const suche = ref('');
const selected = ref(null);
const viewTab = ref('aktiv');

const palettenHeaders = [
  { title: 'Status',       key: 'status',             sortable: true  },
  { title: 'Paletten-Nr.', key: 'paletten_nr',         sortable: true  },
  { title: 'Typ',          key: 'paletten_typ',         sortable: true  },
  { title: 'Lieferschein', key: 'lieferschein',         sortable: true  },
  { title: 'Erstellt am',  key: 'erfassung_datum',      sortable: true  },
  { title: 'Artikel',      key: 'anzahl_positionen',    sortable: false },
  { title: 'Gewicht',      key: 'gesamtgewicht',        sortable: true  },
  { title: 'Aktionen',     key: 'actions',              sortable: false },
];

// Positionen aus dem geladenen Objekt
const positionen = computed(() => selected.value?.positionen || []);

// CMMF um fuehrende Nullen kuerzen (z.B. 00000000210... -> 210...)
function stripCmmf(v) {
  if (v === null || v === undefined) return '';
  return String(v).replace(/^0+/, '') || '0';
}

// IRMAF / "zu erzeugende" Artikel werden mit kein_output = 1 markiert
function istErzeugung(pos) {
  return parseInt(pos.kein_output) === 1;
}

// Normale Positionen und zu erzeugende Artikel getrennt auflisten
const positionenNormal = computed(() => positionen.value.filter(p => !istErzeugung(p)));
const positionenErzeugung = computed(() => positionen.value.filter(p => istErzeugung(p)));

/** Max. Zeilen pro SAP-Clipboard-Paket. */
const SAP_CHUNK_SIZE = 23;

/**
 * Kumulierte Buchungszeilen nach CMMF (IRMAF getrennt).
 * Reihenfolge: zuerst normale Artikel, dann zu erzeugende.
 * @returns {{ cmmf: string, sku: string, menge: number, erzeugung: boolean }[]}
 */
const buchungsZeilen = computed(() => {
  const map = new Map();
  for (const p of positionen.value) {
    const cmmf = stripCmmf(p.cmmf);
    if (!cmmf) continue;
    const erzeugung = istErzeugung(p);
    const key = `${erzeugung ? '1' : '0'}|${cmmf}`;
    const existing = map.get(key);
    const menge = parseInt(p.menge, 10) || 0;
    if (existing) {
      existing.menge += menge;
    } else {
      map.set(key, {
        cmmf,
        sku: p.sku || '',
        menge,
        erzeugung,
      });
    }
  }
  return [...map.values()].sort((a, b) => {
    if (a.erzeugung !== b.erzeugung) return a.erzeugung ? 1 : -1;
    return String(a.cmmf).localeCompare(String(b.cmmf), 'de');
  });
});

const sapChunkIndex = ref(0);
const sapChunkCopied = reactive({ cmmf: false, mengen: false });

const sapChunkCount = computed(() =>
  Math.max(1, Math.ceil(buchungsZeilen.value.length / SAP_CHUNK_SIZE))
);
const sapChunkSlice = computed(() => {
  const start = sapChunkIndex.value * SAP_CHUNK_SIZE;
  return buchungsZeilen.value.slice(start, start + SAP_CHUNK_SIZE);
});
const sapChunkFrom = computed(() =>
  buchungsZeilen.value.length === 0 ? 0 : sapChunkIndex.value * SAP_CHUNK_SIZE + 1
);
const sapChunkTo = computed(() =>
  Math.min((sapChunkIndex.value + 1) * SAP_CHUNK_SIZE, buchungsZeilen.value.length)
);

watch(
  () => selected.value?.id,
  () => {
    sapChunkIndex.value = 0;
    sapChunkCopied.cmmf = false;
    sapChunkCopied.mengen = false;
  }
);

watch(buchungsZeilen, () => {
  if (sapChunkIndex.value >= sapChunkCount.value) {
    sapChunkIndex.value = Math.max(0, sapChunkCount.value - 1);
  }
});

/** Wechselt zum naechsten 23er-Paket (falls vorhanden). */
function advanceSapChunk() {
  if (sapChunkIndex.value < sapChunkCount.value - 1) {
    sapChunkIndex.value += 1;
    sapChunkCopied.cmmf = false;
    sapChunkCopied.mengen = false;
  }
}

/** Wechselt zum vorherigen 23er-Paket. */
function goPrevSapChunk() {
  if (sapChunkIndex.value > 0) {
    sapChunkIndex.value -= 1;
    sapChunkCopied.cmmf = false;
    sapChunkCopied.mengen = false;
  }
}

/**
 * Nach Kopieren von CMMF und Mengen des aktuellen Pakets automatisch weiter.
 */
function maybeAdvanceAfterCopy() {
  if (sapChunkCopied.cmmf && sapChunkCopied.mengen) {
    advanceSapChunk();
  }
}

const gesamtMenge = computed(() =>
  positionen.value.reduce((s, p) => s + (parseInt(p.menge) || 0), 0)
);
const gesamtGewicht = computed(() => {
  const sum = positionen.value.reduce((s, p) => {
    // Dauerhaftes Katalog-Gewicht hat Vorrang vor dem (ueberschreibbaren) Positions-Gewicht
    const kat = parseFloat(p.katalog_gewicht);
    const g = !isNaN(kat) ? kat : (parseFloat(p.gewicht) || 0);
    const m = parseInt(p.menge) || 1;
    return s + g * m;
  }, 0);
  return sum > 0 ? sum : null;
});

// erfassung ausblenden; Archiv-Tab zeigt nur abgeschlossene
const gefiltertePaletten = computed(() => {
  const q = suche.value.trim().toLowerCase();
  return paletten.value
    .filter(p => p.status !== 'erfassung')
    .filter(p => viewTab.value === 'archiv' ? p.status === 'abgeschlossen' : p.status !== 'abgeschlossen')
    .filter(p => !q || [p.paletten_nr, p.lieferschein, p.bestellnummer].some(v => (v || '').toLowerCase().includes(q)));
});

async function load() {
  loading.value = true;
  try {
    // Aktiv und Archiv in einem Call – API liefert alle nicht-erfassung zurück
    const [r1, r2] = await Promise.all([
      axios.get(API, { params: { action: 'get_paletten', tab: 'aktiv' }, withCredentials: true }),
      axios.get(API, { params: { action: 'get_paletten', tab: 'archiv' }, withCredentials: true }),
    ]);
    const aktiv  = r1.data.status === 'success' ? r1.data.paletten : [];
    const archiv = r2.data.status === 'success' ? r2.data.paletten : [];
    // Zusammenführen, Duplikate per id vermeiden
    const seen = new Set();
    const alle = [];
    for (const p of [...aktiv, ...archiv]) {
      if (!seen.has(p.id)) { seen.add(p.id); alle.push(p); }
    }
    paletten.value = alle;
  } catch (e) { /* ignore */ }
  finally { loading.value = false; }
}

async function openPalette(p) {
  try {
    const res = await axios.get(API, { params: { action: 'get_palette', palette_id: p.id }, withCredentials: true });
    if (res.data.status === 'success') selected.value = res.data.palette;
    else toast.error(res.data.message || 'Fehler');
  } catch (e) { toast.error('Palette konnte nicht geladen werden'); }
}

function backToList() {
  selected.value = null;
  load();
}

/** Aktuelles 23er-Paket: CMMF-Zeilen in die Zwischenablage. */
function copyCmmfChunk() {
  const slice = sapChunkSlice.value;
  if (!slice.length) {
    toast.warning('Keine CMMF vorhanden');
    return;
  }
  const text = slice.map((p) => p.cmmf).join('\n');
  navigator.clipboard.writeText(text).then(() => {
    const label =
      sapChunkIndex.value === 0
        ? `Erste ${slice.length} CMMF kopiert`
        : `Nächste ${slice.length} CMMF kopiert (Paket ${sapChunkIndex.value + 1}/${sapChunkCount.value})`;
    toast.success(label);
    sapChunkCopied.cmmf = true;
    maybeAdvanceAfterCopy();
  });
}

/** Aktuelles 23er-Paket: Mengen in derselben Reihenfolge wie CMMF. */
function copyMengenChunk() {
  const slice = sapChunkSlice.value;
  if (!slice.length) {
    toast.warning('Keine Positionen vorhanden');
    return;
  }
  const text = slice.map((p) => p.menge).join('\n');
  navigator.clipboard.writeText(text).then(() => {
    const label =
      sapChunkIndex.value === 0
        ? `Erste ${slice.length} Mengen kopiert`
        : `Nächste ${slice.length} Mengen kopiert (Paket ${sapChunkIndex.value + 1}/${sapChunkCount.value})`;
    toast.success(label);
    sapChunkCopied.mengen = true;
    maybeAdvanceAfterCopy();
  });
}

// Gewicht pro CMMF dauerhaft im Katalog speichern (refurbish_artikel_katalog)
const gewichtDialog = ref(false);
const gewichtPos = ref(null);
const neuesGewicht = ref(null);
const gewichtSaving = ref(false);

function gewichtNachtragen(pos) {
  if (!pos?.cmmf) { toast.warning('Keine CMMF – Gewicht kann nicht gespeichert werden'); return; }
  gewichtPos.value = pos;
  neuesGewicht.value = parseFloat(pos.katalog_gewicht) || null;
  gewichtDialog.value = true;
}

async function gewichtSpeichern() {
  if (!gewichtPos.value) return;
  const cmmf = gewichtPos.value.cmmf;
  const g = parseFloat(neuesGewicht.value);
  if (isNaN(g) || g <= 0) { toast.error('Bitte gültiges Gewicht eingeben'); return; }
  gewichtSaving.value = true;
  try {
    const res = await axios.post(API, {
      action: 'set_cmmf_gewicht',
      cmmf,
      gewicht: g,
      palette_id: selected.value?.id,
    }, { withCredentials: true });
    if (res.data.status === 'success') {
      // Alle Positionen derselben CMMF synchron halten
      positionen.value.forEach(p => {
        if (p.cmmf === cmmf) { p.katalog_gewicht = res.data.gewicht; p.gewicht = res.data.gewicht; }
      });
      toast.success('Gewicht gespeichert');
      gewichtDialog.value = false;
    } else {
      toast.error(res.data.message || 'Fehler beim Speichern');
    }
  } catch (e) {
    toast.error(e.response?.data?.message || 'Gewicht speichern fehlgeschlagen');
  } finally {
    gewichtSaving.value = false;
  }
}

async function speichern() {
  if (!selected.value) return;
  saving.value = true;
  try {
    const r1 = await axios.post(API, {
      action: 'update_lieferschein',
      palette_id: selected.value.id,
      belegdatum: selected.value.belegdatum || '',
      lieferschein: selected.value.lieferschein || '',
      bestellnummer: selected.value.bestellnummer || '',
    }, { withCredentials: true });
    if (r1.data.status !== 'success') { toast.error(r1.data.message || 'Fehler beim Speichern'); return; }
    await axios.post(API, {
      action: 'add_note',
      palette_id: selected.value.id,
      feld: 'office_notiz',
      notiz: selected.value.office_notiz || '',
    }, { withCredentials: true });
    toast.success('Gespeichert');
  } catch (e) { toast.error('Speichern fehlgeschlagen'); }
  finally { saving.value = false; }
}

async function saveNote() {
  if (!selected.value) return;
  try {
    await axios.post(API, {
      action: 'add_note',
      palette_id: selected.value.id,
      feld: 'office_notiz',
      notiz: selected.value.office_notiz || '',
    }, { withCredentials: true });
  } catch (e) { /* ignore silent autosave */ }
}

async function statusWechsel(status, msg) {
  try {
    const res = await axios.post(API, {
      action: 'update_palette_status',
      palette_id: selected.value.id,
      status,
    }, { withCredentials: true });
    if (res.data.status === 'success') { toast.success(msg); backToList(); }
    else toast.error(res.data.message || 'Fehler');
  } catch (e) { toast.error('Statuswechsel fehlgeschlagen'); }
}

const freigabeVersand = () => statusWechsel('versand_final', 'An Versand freigegeben');
const zurueck        = () => statusWechsel('erfassung', 'Zurück an DTC');

onMounted(load);
</script>

<style scoped>
.dtc-office-panel { margin-top: 8px; }
.status-overview-card { border-radius: 12px; }
.palette-link {
  cursor: pointer;
  font-weight: 600;
  text-decoration: none;
}
.palette-link:hover { text-decoration: underline; }
.form-field { border-radius: 8px; }
.erzeugung-trenner td {
  background: rgba(255, 152, 0, 0.12);
  border-top: 2px solid rgba(255, 152, 0, 0.4);
  color: #ffb74d;
}
.erzeugung-row td { background: rgba(255, 152, 0, 0.05); }
</style>
